import * as React from "react";
import { Button, Grid, Stack, Tooltip, tooltipClasses } from "@mui/material";
import { Link as RouteLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import DateRangeIcon from "@mui/icons-material/DateRange";
import styled from "styled-components";
import AvatarBadge from "src/components/avatar-badge";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import Icon from "src/components/icon";
import githubIcon from "src/assets/icons/github-int";
import gitlabIcon from "src/assets/icons/gitlab-int";
import slackIcon from "src/assets/icons/slack-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import googleWorkspaceIcon from "src/assets/icons/google-workspace-int";
import RangePickerModal from "src/components/range-picker-modal";
import jiraIcon from "src/assets/icons/jira-int";
import notionIcon from "src/assets/icons/notion-int";
import { Helmet } from "react-helmet";
import { TeamSprintsContextProvider } from "src/scenes/teams/dashboard/selected-sprints-provider-teams";
import JoinLeftIcon from "@mui/icons-material/JoinLeft";
import {
    EditModes,
    useTemplateViewEdit,
} from "src/components/providers/template-view-edit-provider";
import TemplateDashboardTitle from "src/components/template/template-dashboard-title";
import Typography from "@mui/material/Typography";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useTeam } from "../team/team-provider";

const NavButton = styled(Button).withConfig({
    shouldForwardProp: (prop) => prop !== "active",
})`
    & > h1 {
        font-size: 13px;
        margin: 0;
        padding: 0;
    }
    font-size: 13px;
    min-height: 41px;
    text-transform: none;
    cursor: pointer;
    margin: 12px;
    min-width: 126px;
    color: ${({ theme, active }) =>
        active ? theme.palette.primary.main : theme.palette.text.primary};
    border: 1px solid
        ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
    border-radius: 6px;

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        border: 1px solid
            ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
        background-color: transparent;
    }
`;

const UnregisteredIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 42px;
    color: ${({ theme }) => theme.palette.background.default};
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 50%;
`;

const StyledAvatarBadge = styled(AvatarBadge)`
    box-shadow: 0 4px 4px 0 #00000040;
    margin-left: ${({ firstElement }) => (firstElement ? "0" : "-8px")};
    z-index: 1;
    cursor: pointer;
    outline: ${({ isIntegrated, theme }) =>
        isIntegrated.gitlab || isIntegrated.github || isIntegrated.slack
            ? `solid 2px ${theme.palette.primary.main}`
            : `solid 2px ${theme.palette.background.inactiveIcons}`};
    &:hover {
        z-index: 2;
    }
`;

const Link = styled(RouteLink)`
    font-size: 12px;
    font-weight: 400;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

const TeamName = styled.h1`
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
    font-style: normal;
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const TeamNameLink = styled(RouteLink)`
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
    font-style: normal;
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const SectionHelper = styled.p`
    font-size: 12px;
    font-style: normal;
    font-weight: 200;
    opacity: 0.7;
    padding-right: 12px;
    margin: 0;
`;

const DatepickerButton = styled(Button)`
    font-size: 12px;
    width: 257px;
    z-index: 10;
`;

const LightTooltip = styled(({ className, isExtra, title, children, ...props }) => (
    <Tooltip {...props} title={title} classes={{ popper: className }}>
        {children}
    </Tooltip>
))(({ theme, isExtra }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.background.tooltip,
        border: `4px solid ${theme.palette.background.outline}`,
        borderRadius: 14,
        fontSize: 11,
        minWidth: isExtra ? "336px" : "300px",
    },
}));

const TeamDashboard = () => {
    const [openDatepicker, setOpenDatepicker] = React.useState(false);
    const { teamId } = useParams();
    const { pathname } = useLocation();
    const pathnameSplit = pathname.split("/");
    const path = pathnameSplit && pathnameSplit[pathnameSplit.length - 1];
    const teamApi = useTeam();

    const { onEditMode, view, editView } = useTemplateViewEdit();
    const mergeMembersAndTempMembers = () => {
        if (teamApi && teamApi.status !== "SUCCESS") return null;
        const { members, tempMembers } = teamApi && teamApi.data;
        const normalizedTempMembers = tempMembers.map((temp) => ({
            displayName: temp.displayName,
            email: temp.email,
            isOwner: false,
            isIntegrated: temp.isIntegrated,
            jobTitle: temp.jobTitle || null,
            picture: null,
            id: null,
            isInvited: temp.isInvited,
        }));

        const combinedUsers = [...members, ...normalizedTempMembers];
        return combinedUsers;
    };

    const allMembers = mergeMembersAndTempMembers();

    const teamMembersToShow = allMembers ? allMembers.slice(0, 4) : [];
    const teamMembersHidden = allMembers ? allMembers.slice(4) : [];

    const toggleDatepicker = () => setOpenDatepicker(!openDatepicker);

    const navigate = useNavigate();

    const redirectToTemplatesDetails = () => {
        return navigate("/templates-details");
    };

    const tooltipContent = () => {
        return teamMembersHidden.map((member) => (
            <Stack direction="row" key={member.email} padding={2} alignItems="center" spacing={2}>
                {member.id ? (
                    <StyledAvatarBadge
                        name={member.displayName || member.email}
                        picture={member.picture}
                        isIntegrated={member.isIntegrated}
                        d
                    />
                ) : (
                    <UnregisteredIconWrapper>
                        <JoinLeftIcon />
                    </UnregisteredIconWrapper>
                )}
                <Stack direction="column">
                    <SectionHelper sx={{ marginLeft: "60px" }}>
                        {member.displayName || member.email}
                    </SectionHelper>
                    <Stack direction="row" spacing={2} pt={0.5}>
                        <Icon
                            svg={gitlabIcon}
                            fill={
                                member.isIntegrated.gitlab === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={gitlabSelfManagedIcon}
                            fill={
                                member.isIntegrated.gitlabSelfManaged === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={githubIcon}
                            fill={
                                member.isIntegrated.github === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={slackIcon}
                            fill={
                                member.isIntegrated.slack === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={googleWorkspaceIcon}
                            fill={
                                member.isIntegrated.google === true || member.isIntegrated.microsoft
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={jiraIcon}
                            fill={
                                member.isIntegrated.jira === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                        <Icon
                            svg={notionIcon}
                            fill={
                                member.isIntegrated.notion === true
                                    ? "palette.primary.main"
                                    : "palette.background.inactiveIcons"
                            }
                        />
                    </Stack>
                </Stack>
            </Stack>
        ));
    };
    return (
        <TeamSprintsContextProvider>
            <Helmet>
                {teamApi.data && teamApi.data.name && (
                    <title>Adadot - {teamApi.data && teamApi.data.name}&apos;s Dashboard</title>
                )}
            </Helmet>
            <Box sx={{ width: "100%", typography: "body1" }}>
                <Grid container justifyContent="space-between" alignItems="center" m={2} mt={0}>
                    <Grid item>
                        <ShowWithSkeleton
                            fetchAction={teamApi}
                            height={25}
                            width={150}
                            variant={undefined}
                            streamingProperty={undefined}
                        >
                            <TeamNameLink to={`/teams/${teamId}`}>
                                <TeamName>{teamApi.data && teamApi.data.name}</TeamName>
                            </TeamNameLink>
                        </ShowWithSkeleton>
                    </Grid>
                    <Grid item>
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Stack direction="row" alignItems="center">
                                <SectionHelper> Showing data for: </SectionHelper>
                                {teamMembersToShow.map((member, index) => (
                                    <LightTooltip
                                        key={member.id}
                                        title={
                                            <Stack direction="column" padding={2}>
                                                {member.displayName || member.email || ""}
                                                {member.jobTitle ? ` is a ${member.jobTitle}` : ""}
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    pt={0.5}
                                                    justifyContent="center"
                                                >
                                                    <Icon
                                                        svg={gitlabIcon}
                                                        fill={
                                                            member.isIntegrated.gitlab === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={gitlabSelfManagedIcon}
                                                        fill={
                                                            member.isIntegrated
                                                                .gitlabSelfManaged === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={githubIcon}
                                                        fill={
                                                            member.isIntegrated.github === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={slackIcon}
                                                        fill={
                                                            member.isIntegrated.slack === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={googleWorkspaceIcon}
                                                        fill={
                                                            member.isIntegrated.google === true ||
                                                            member.isIntegrated.microsoft
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={jiraIcon}
                                                        fill={
                                                            member.isIntegrated.jira === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                    <Icon
                                                        svg={notionIcon}
                                                        fill={
                                                            member.isIntegrated.notion === true
                                                                ? "palette.primary.main"
                                                                : "palette.background.inactiveIcons"
                                                        }
                                                    />
                                                </Stack>
                                            </Stack>
                                        }
                                    >
                                        <span>
                                            {member.id ? (
                                                <StyledAvatarBadge
                                                    name={member.displayName || member.email}
                                                    picture={member.picture}
                                                    isIntegrated={member.isIntegrated}
                                                    firstElement={index === 0}
                                                />
                                            ) : (
                                                <UnregisteredIconWrapper>
                                                    <JoinLeftIcon />
                                                </UnregisteredIconWrapper>
                                            )}
                                        </span>
                                    </LightTooltip>
                                ))}
                            </Stack>
                            {teamMembersHidden.length > 0 && (
                                <LightTooltip
                                    title={tooltipContent()}
                                    placement="bottom-end"
                                    isExtra
                                >
                                    <Link to={`/teams/${teamId}`}>
                                        +{teamMembersHidden.length} more members
                                    </Link>
                                </LightTooltip>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" justifyContent="space-between">
                    <Grid
                        item
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "auto" }}
                    >
                        <Grid item md={12}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ width: "100%" }}
                            >
                                <Typography style={{ fontSize: "13px" }}>
                                    Team dashboards
                                </Typography>
                                {path !== "insights" && (
                                    <DatepickerButton
                                        startIcon={<DateRangeIcon />}
                                        onClick={toggleDatepicker}
                                    >
                                        Select Dates / Sprint
                                    </DatepickerButton>
                                )}
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            sx={{ width: "auto" }}
                            md={9}
                        >
                            {!onEditMode && (
                                <NavButton
                                    variant="outlined"
                                    active={path === "insights"}
                                    component={RouteLink}
                                    to={`/teams/${teamId}/dashboard/insights`}
                                >
                                    {path === "insights" ? <h1>Insights</h1> : <>Insights</>}
                                </NavButton>
                            )}
                            {view &&
                                view.dashboards.map((dashboard, dashboardIndex) => (
                                    <TemplateDashboardTitle
                                        dashboardIndex={dashboardIndex}
                                        dashboard={dashboard}
                                        key={dashboard.id}
                                    />
                                ))}
                            {onEditMode && view && (
                                <Button
                                    startIcon={<PlaylistAddCircleIcon />}
                                    variant="contained"
                                    size="small"
                                    style={{ borderRadius: "24px", marginLeft: "26px" }}
                                    onClick={() => {
                                        if (editView) {
                                            editView({
                                                dashboardIndex: view.dashboards.length,
                                                editMode: EditModes.addDashboard,
                                            });
                                        }
                                    }}
                                >
                                    ADD DASHBOARD
                                </Button>
                            )}
                        </Grid>
                        <Grid item md={3}>
                            <Grid container alignItems="center" justifyContent="flex-end">
                                {!onEditMode ? (
                                    <Grid item>
                                        <Stack
                                            direction="column"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            marginRight={1}
                                        >
                                            <Stack
                                                direction="row"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                <Button
                                                    startIcon={<ViewListIcon />}
                                                    onClick={() => {
                                                        redirectToTemplatesDetails();
                                                    }}
                                                    style={{ width: "257px", fontSize: "12px" }}
                                                >
                                                    View all templates
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Outlet />
                <RangePickerModal
                    open={openDatepicker}
                    handleClose={() => setOpenDatepicker(false)}
                    toggleDatepicker={toggleDatepicker}
                    isRepo={false}
                    isTeams
                />
            </Box>
        </TeamSprintsContextProvider>
    );
};

export default TeamDashboard;
